
import { Component, Vue, Watch, Prop, Ref } from "vue-property-decorator"
import { CaseColumn, isBuiltInCaseColumn } from "@/types/caseColumns"

// @ts-ignore
import draggable from "vuedraggable"
import vSelect from "vue-select"
import VuePerfectScrollbar from "vue-perfect-scrollbar"

import ColumnFormatting from "./ColumnFormatting.vue"
import { DataType, FormattingRule, ColumnOptions, editableFieldTypes } from "@/types/insights"
import { InsightCalculationColumn } from "@/types/insights/calculation"
import ExpressionValidation from "@/scripts/validation/expressionValidation"
import { State } from "vuex-class"
import DefinitionTitle from "../Config/Definitions/DefinitionTitle.vue"
import { inlineEditMode } from "@/types/insights/selectiveInlineEdit"
import { TenantFeatures } from "@/types/tenantFeatures"

@Component({
	components: {
		draggable,
		"v-select": vSelect,
		"scrollbar": VuePerfectScrollbar,
		"column-formatting": ColumnFormatting,
		"definition-title": DefinitionTitle
	}
})
export default class ColumnSelect extends Vue {
	@Ref() calculationColumnTitleInput!: HTMLInputElement

	@Prop({ default: () => [] })
	columns!: CaseColumn[]

	@Prop({ default: () => [] })
	calculationColumnFields!: CaseColumn[]

	@Prop({ default: () => [] })
	calculationColumns!: InsightCalculationColumn[]

	@Prop({ default: () => [] })
	value!: string[]

	@Prop({ default: () => { return {} } })
	columnOptions!: { [key: string]: ColumnOptions }

	@Prop({ default: () => { return {} } })
	formattingRules!: { [key: string]: FormattingRule[] }

	@Prop({ default: () => inlineEditMode.None })
	currentInlineEditMode!: inlineEditMode

	@Prop({ default: () => false })
	areColumnsForInsight!: boolean

	@State tenantEnabledFeatures!: { [key: string]: boolean }

	inlineEditMode = inlineEditMode

	currentValue: string[] = []
	currentColumnOptions: { [key: string]: ColumnOptions } | null = null
	currentFormattingRules: { [key: string]: FormattingRule[] } | null = null

	addColumn: CaseColumn | null = null
	editFormattingColumn: CaseColumn | null = null
	isCalculationColumnModalVisible: Boolean = false
	calculationColumnModalMode: string = ""
	calculationColumn: InsightCalculationColumn = {
		key: "",
		title: "",
		expression: null
	}

	get visible (): boolean {
		return !!this.editFormattingColumn
	}
	set visible (value: boolean) {
		if (!value) {
			this.editFormattingColumn = null
			this.$emit("hide")
		}
	}

	mounted () {
		// Add on the list of calculationColumnFields to our existing columns
		this.currentValue = this.value.filter(c => this.insightColumns.some(column => column.key === c))

		if (this.currentValue.length < this.value.length) {
			this.$emit("input", this.currentValue)
		}
		this.currentColumnOptions = this.columnOptions ? this.columnOptions : {}
		this.currentFormattingRules = this.formattingRules ? this.formattingRules : {}
	}

	get insightCalculationColumnFeatureEnabled (): boolean {
		return this.areColumnsForInsight && this.tenantEnabledFeatures[TenantFeatures.InsightCalculationColumn]
	}

	get selectiveInlineEditFeatureEnabled (): boolean {
		return this.tenantEnabledFeatures[TenantFeatures.SelectiveInlineEdit]
	}

	get isBuiltInFieldsInlineEditFeatureEnabled (): boolean {
		return this.tenantEnabledFeatures[TenantFeatures.BuiltInFieldsInlineEdit] ?? false
	}

	get isDivisionByZeroFormattingFeatureEnabled (): boolean {
		return this.tenantEnabledFeatures[TenantFeatures.DivisionByZeroFormatting] ?? false
	}

	get inlineEditingEnabled () {
		if (this.editFormattingColumn) {
			return this.currentColumnOptions![this.editFormattingColumn!.key]?.inlineEdit
		}
		return false
	}

	get insightColumns (): CaseColumn[] {
		return this.columns.concat(this.calculationColumnFields)
	}

	@Watch("value")
	onValueChanged (newValue: string[]) {
		this.currentValue = newValue
	}

	@Watch("columnOptions")
	onColumnOptionsChanged (columnOptions: { [key: string]: ColumnOptions } | null) {
		this.currentColumnOptions = columnOptions || {}
	}

	@Watch("formattingRules")
	onFormattingRulesChanged (formattingRules: { [key: string]: FormattingRule[] } | null) {
		this.currentFormattingRules = formattingRules || {}
	}

	get dragOptions () {
		return {
			animation: 200,
			disabled: false,
			ghostClass: "ghost"
		}
	}

	get columnsByKey (): { [key: string]: CaseColumn } {
		return this.insightColumns.toDictionary("key")
	}

	get unaddedColumns (): CaseColumn[] {
		return this.insightColumns.filter(c => !this.currentValue.includes(c.key))
	}

	get columnValues () {
		const columnsByKey = this.columnsByKey
		return this.currentValue.map(col => {
			if (columnsByKey[col]) {
				return columnsByKey[col]
			} else {
				return null
			}
		}).filter(col => !!col)
	}

	get editFormattingRules (): FormattingRule[] {
		return this.editFormattingColumn
			? this.currentFormattingRules![this.editFormattingColumn!.key]
			: []
	}

	dragEnd () {
		this.$emit("input", this.currentValue)
	}

	addNewColumn (newColumn: CaseColumn | null): void {
		if (newColumn) {
			this.currentValue.push(newColumn.key)
			this.$emit("input", this.currentValue)
		}
		this.addColumn = null
	}

	removeColumn (column: CaseColumn): void {
		if (this.isCalculationColumn(column.key)) {
			// Get the calculation column based off the key provided
			var calculationColumn = this.calculationColumns.find(calculationColumn => {
				return calculationColumn.key === column.key
			})

			// Set the calculationColumn and display the modal
			if (calculationColumn) {
				this.calculationColumn = calculationColumn
				this.calculationColumnModalMode = "delete"
				this.isCalculationColumnModalVisible = true
			}
		} else {
			this.currentValue.removeWhere(key => key === column.key)
			this.$emit("input", this.currentValue)

			if (this.currentFormattingRules && this.currentFormattingRules[column.key]) {
				delete this.currentFormattingRules[column.key]
				this.$emit("inputFormatting", this.currentFormattingRules)
			}
		}
	}

	removeCalculationColumn () {
		// Remove calculation column & close modal
		this.$emit("calculationColumnDelete", this.calculationColumn)
		this.closeCalculationColumnModal()
	}

	editCalculationColumn (column: CaseColumn) {
		// Get the calculation column based off the key provided
		var calculationColumn = this.calculationColumns.find(calculationColumn => {
			return calculationColumn.key === column.key
		})

		if (calculationColumn) {
			// Set the calculationColumn and display the modal
			this.calculationColumn = {
				key: column.key,
				title: calculationColumn.title,
				expression: calculationColumn.expression
			}

			this.calculationColumnModalMode = "edit"
			this.isCalculationColumnModalVisible = true
		}
	}

	saveCalculationColumnModal () {
		// Save calculation column & close modal
		this.$emit("calculationColumnSave", this.calculationColumn)
		this.closeCalculationColumnModal()
	}

	setFormatting (column: CaseColumn) {
		if (!this.currentFormattingRules![column.key]) {
			Vue.set(this.currentFormattingRules!, column.key, [])
		}
		this.editFormattingColumn = column
	}

	updateSettings (value:any) {
		if (this.editFormattingColumn) {
			let newInlineEdit: ColumnOptions = {
				inlineEdit: value
			}
			Vue.set(this.currentColumnOptions!, this.editFormattingColumn.key, newInlineEdit)
		}

		this.$emit("inputSettings", this.currentColumnOptions)
	}

	updateFormattingRules (rules: FormattingRule[]) {
		if (this.editFormattingColumn) {
			Vue.set(this.currentFormattingRules!, this.editFormattingColumn.key, rules)
		}
		this.$emit("inputFormatting", this.currentFormattingRules)
	}

	supportsFormatting (fieldDataType: DataType) {
		return fieldDataType !== DataType.arrayData && fieldDataType !== DataType.none && fieldDataType !== DataType.mapPoint
	}

	supportsInlineEditing (column: CaseColumn) {
		if (!column) return false

		return this.currentInlineEditMode === inlineEditMode.Some && editableFieldTypes.has(column.dataType) &&
			((isBuiltInCaseColumn(column.key) && !column.isReadonly && this.isBuiltInFieldsInlineEditFeatureEnabled) || column.dynamicField) &&
			!this.isCalculationColumn(column.key)
	}

	supportsColumnOptions (column?: CaseColumn): boolean {
		if (!column) return false

		return this.supportsFormatting(column.fieldDataType!) || this.supportsInlineEditing(column)
	}

	private showColumnOptions (column: CaseColumn) {
		return this.supportsColumnOptions(column)
	}

	createCalculationColumn () {
		this.calculationColumn = {
			key: "",
			title: "",
			expression: null
		}
		this.calculationColumnModalMode = "new"
		this.isCalculationColumnModalVisible = true

		this.$nextTick(async () => {
			for (let i = 0; i < 3; i++) {
				await this.$nextTick() // this needs 3 ticks to work due to modal opening
			}
			this.calculationColumnTitleInput.focus()
		})
	}

	isCalculationColumn (key: string) {
		return key.startsWith("calculation.")
	}

	calculationColumnModalTitle () {
		var title
		if (this.calculationColumnModalMode === "new") {
			title = "New"
		} else if (this.calculationColumnModalMode === "edit") {
			title = "Edit"
		} else if (this.calculationColumnModalMode === "delete") {
			title = "Delete"
		}
		return title + " Calculation Column"
	}

	get isCalculationColumnValid () : boolean {
		// Checks the modal data is valid
		return !(this.calculationColumn.title.trim().length > 0 &&
			this.calculationColumn.expression != null &&
			this.calculationColumn.expression?.trim().length > 0 &&
			this.isValidMathExpression)
	}

	get isValidMathExpression (): boolean {
		if (this.calculationColumn.expression != null) {
			return ExpressionValidation.isValidMathExpression(this.calculationColumn.expression)
		} else {
			return false
		}
	}

	closeCalculationColumnModal () {
		// Reset values and close modal
		this.calculationColumn = {
			key: "",
			title: "",
			expression: null
		}
		this.isCalculationColumnModalVisible = false
	}

	private get calculationOptions () :any[] {
		let options: any[] = []
		// Only include standard columns - we do not want to include calculation columns
		// Also exclude the DaysOpen built in column
		this.columns.forEach(column => {
			if (column.key && column.label && column.key.toLowerCase() !== "openfordays" &&
					(column.dataType === "number" || column.dataType === "user-role")) {
				options.push({
					field: {
						id: column.key,
						title: column.label
					}
				})
			}
		})
		return options
	}
}

